import { useEffect, useRef, useState, type ReactNode } from 'react';
import { format } from 'date-fns';
import { ServerCog } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/input';
import { PaginationControls } from '@/components/ui/pagination';
import { useToolExecutionLogs } from '@/hooks';
import { ConversationTicketLink } from '@/modules/integrations/components/conversation-ticket-link';
import { ToolExecutionLogDetailsSidebar } from '@/modules/integrations/components/tool-execution-log-details-sidebar';
import { SettingsSelect, SettingsTableRows, type SettingsColumn } from '@/modules/settings';
import type { ToolExecutionLog } from '@/services';
import { TOOL_SERVER_LOG_COLUMNS, useToolServerSelection, type ToolServerLogFilters } from './tool-server-log-filters';

const FILTER_TRIGGER_CLASS = 'h-8 w-[170px] rounded-full border border-gray-500 bg-white text-[12px]';

const jsonPreview = (value: unknown) => <span className='truncate'>{value == null ? '—' : JSON.stringify(value)}</span>;

const TOOL_SERVER_LOG_CELLS: Record<string, (log: ToolExecutionLog) => ReactNode> = {
	at: (log) => <span className='truncate tabular-nums'>{format(new Date(log.createdAt), 'd MMM yyyy, HH:mm')}</span>,
	requestId: (log) => (
		<span className='truncate font-medium' title={log.requestId || undefined}>
			{log.requestId || '—'}
		</span>
	),
	conversation: (log) => <ConversationTicketLink log={log} />,
	input: (log) => jsonPreview(log.input),
	output: (log) => jsonPreview(log.output),
	latency: (log) => <span className='tabular-nums'>{log.latencyMs} ms</span>,
	// Provider errors still return 200, so the colour comes from status, not the code.
	statusCode: (log) => (
		<Badge variant={log.status === 'success' ? 'success' : 'destructive'} title={log.errorCode ?? undefined}>
			{log.statusCode}
		</Badge>
	)
};

const TOOL_SERVER_LOG_TABLE: SettingsColumn<ToolExecutionLog>[] = TOOL_SERVER_LOG_COLUMNS.map((head) => ({
	...head,
	cell: TOOL_SERVER_LOG_CELLS[head.id]
}));

/** Header tools: the integration, one of its actions, and the request-ID search. */
export function ToolServerLogTools({ filters }: { filters: ToolServerLogFilters }) {
	const { providers, actions, provider, tool } = useToolServerSelection(filters);
	return (
		<>
			<SettingsSelect
				value={provider ?? ''}
				onValueChange={filters.setProvider}
				options={providers}
				placeholder='Integration'
				disabled={providers.length === 0}
				aria-label='Integration'
				className={FILTER_TRIGGER_CLASS}
			/>
			<SettingsSelect
				value={tool ?? ''}
				onValueChange={(next) => provider && filters.setTool(provider, next)}
				options={actions.map((a) => ({ value: a.tool, label: a.label }))}
				placeholder='Action'
				disabled={actions.length === 0}
				aria-label='Action'
				className={FILTER_TRIGGER_CLASS}
			/>
			<SearchInput
				value={filters.query}
				onChange={(e) => filters.setQuery(e.target.value)}
				placeholder='Search by request ID'
				aria-label='Search by request ID'
				className='h-8 w-[220px] border-gray-500 bg-white'
			/>
		</>
	);
}

/** Logs › Tool Logs: one action's recorded calls on the tool server, newest first, paged. A row opens the call's details. */
export function ToolServerLog({ filters }: { filters: ToolServerLogFilters }) {
	const selection = useToolServerSelection(filters);
	const logsQuery = useToolExecutionLogs(selection.provider ?? '', selection.tool ?? '', filters.requestId, filters.page);
	const logs = logsQuery.data?.logs ?? [];
	const [open, setOpen] = useState<ToolExecutionLog | null>(null);
	const panelRef = useRef<HTMLDivElement>(null);

	// Close the panel on a click anywhere outside it.
	useEffect(() => {
		if (!open) return;
		const handleOutsideClick = (event: MouseEvent) => {
			if (!panelRef.current?.contains(event.target as Node)) setOpen(null);
		};
		document.addEventListener('mousedown', handleOutsideClick);
		return () => document.removeEventListener('mousedown', handleOutsideClick);
	}, [open]);

	if (selection.isError || logsQuery.isError) {
		return (
			<div className='flex flex-col items-center gap-3 py-12'>
				<p className='text-[13px] text-gray-900'>The tool logs could not be loaded.</p>
				<Button variant='outline' size='sm' onClick={() => void (selection.isError ? selection.refetch() : logsQuery.refetch())}>
					Retry
				</Button>
			</div>
		);
	}

	const [title, hint] = !selection.isLoading && !selection.hasConnected
		? ['No connected integrations', 'Connect one in Settings › Integrations and its actions show here to pick from.']
		: !selection.isLoading && !selection.tool
			? ['No integration actions yet', 'None of the connected integrations has actions on the tool server yet.']
			: filters.requestId
				? ['Nothing matches', 'No call of this action has this request ID.']
				: ['No calls recorded yet', 'When Kim runs this action, each call shows here with its input, output and how long it took.'];

	return (
		<>
			<SettingsTableRows
				columns={TOOL_SERVER_LOG_TABLE}
				rows={logs}
				rowKey={(log) => String(log.id)}
				isLoading={selection.isLoading || logsQuery.isLoading}
				onRowClick={setOpen}
				empty={
					<div className='flex flex-col items-center gap-3'>
						<span className='flex size-10 items-center justify-center rounded-full bg-indigo-50'>
							<ServerCog className='size-4 text-indigo-600' />
						</span>
						<p className='text-[13px] font-medium text-ink'>{title}</p>
						<p className='max-w-[420px] text-[12px] leading-4 text-gray-900'>{hint}</p>
					</div>
				}
			/>
			{logsQuery.data && (
				<PaginationControls page={logsQuery.data.pagination.page} totalPages={logsQuery.data.pagination.totalPages} onPageChange={filters.setPage} />
			)}
			{open && (
				// Slides over the page, like the call details on an action's page, instead of pushing the rows aside.
				<div
					ref={panelRef}
					className='fixed inset-y-0 right-0 z-30 flex shadow-[-16px_0_40px_-16px_rgba(16,24,40,0.22)] animate-in slide-in-from-right duration-200'
				>
					<ToolExecutionLogDetailsSidebar log={open} onClose={() => setOpen(null)} />
				</div>
			)}
		</>
	);
}
